(() => {
  'use strict';
  if (window.KMCartAnalytics) return;

  const scopes = '#CartDrawer, [data-km-cart-page]';
  const cards = '[data-km-recommendation-product], [data-km-cart-pair-product]';
  const views = new WeakMap();
  const exposed = new Set();
  const intersections = new Map();
  const exposureTimers = new Map();
  const recommendationExposed = new Set();
  const navigations = new WeakMap();
  let frame = null;

  // Analytics must never interrupt a cart mutation or navigation.
  function track(name, parameters = {}) {
    try {
      const payload = {...parameters, transport_type: 'beacon'};
      if (typeof window.gtag === 'function') window.gtag('event', name, payload);
      else {
        window.dataLayer = window.dataLayer || [];
        (function () { window.dataLayer.push(arguments); })('event', name, payload);
      }
      return true;
    } catch (error) { return false; }
  }

  // 统一使用商品 ID；平台与站内 GA4 约定保持一致，不能用规格 ID 代替商品 ID。
  function commerce(name, value, extra = {}, event) {
    try {
      const node = value instanceof Element ? value : null;
      const productId = node ? node.dataset.kmProductId || node.dataset.kmCartProductId
        || node.dataset.kmRecommendationProduct : value?.product_id;
      if (productId == null || !String(productId).trim()) return false;
      const parameters = {product_id: String(productId),
        platform: window.KMGetGaDeviceType?.() || (window.matchMedia('(max-width: 768px)').matches ? 'M' : 'PC'), ...extra};
      const link = event?.target?.closest?.('a[href]');
      if (link && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey
        && (!link.target || link.target === '_self') && !link.hasAttribute('download')
        && /^https?:$/.test(link.protocol)) {
        let complete;
        const promise = new Promise(resolve => { complete = resolve; });
        const timer = setTimeout(complete, 800);
        parameters.event_callback = () => { clearTimeout(timer); complete(); };
        parameters.event_timeout = 800;
        navigations.set(event, {link, promise});
      }
      const sent = track(name, parameters);
      if (!sent && event) navigations.delete(event);
      return sent;
    } catch (error) { return false; }
  }

  function product(value) {
    if (!value) return null;
    const node = value instanceof Element ? value : null;
    const card = node?.matches(cards) ? node : node?.closest(cards);
    const link = node?.querySelector('.cart__item-name, .km-cart-selected-product__title, .km-cart-recommendations__title, .km-cart-pair__title, a[href]');
    let handle = node ? node.dataset.kmProductHandle || node.dataset.kmCartProductHandle || node.dataset.kmPairHandle
      : value.handle || value.product_handle || value.product?.handle;
    if (!handle) {
      try { handle = decodeURIComponent(new URL(link?.href || value.url, location.origin).pathname.match(/\/products\/([^/]+)/)?.[1] || ''); }
      catch (error) { handle = ''; }
    }
    const title = node ? node.dataset.kmProductTitle || node.dataset.kmCartProductTitle || link?.textContent
      : value.product_title || value.title || value.product?.title;
    if (!handle || !title) return null;
    const parameters = {product_handle: String(handle), product_title: String(title).trim()};
    const productId = node ? node.dataset.kmCartProductId || node.dataset.kmProductId || card?.dataset.kmProductId : value.product_id;
    const price = node ? card?.dataset.kmProductPrice : value.price;
    const currency = node ? card?.dataset.kmProductCurrency : value.currency;
    if (productId != null && String(productId).trim()) parameters.product_id = String(productId);
    if (price != null && price !== '' && Number.isFinite(Number(price))) parameters.price = Number(price);
    if (currency) parameters.currency = String(currency);
    if (card?.dataset.kmRecommendationModule || value.recommendation_module) {
      parameters.recommendation_module = card?.dataset.kmRecommendationModule || value.recommendation_module;
    }
    if (card?.dataset.kmRecommendationScene || value.recommendation_scene) {
      parameters.recommendation_scene = card?.dataset.kmRecommendationScene || value.recommendation_scene;
    }
    if (card?.dataset.kmRecommendationBatchId || value.recommendation_batch_id) {
      parameters.recommendation_batch_id = card?.dataset.kmRecommendationBatchId || value.recommendation_batch_id;
    }
    const position = card?.dataset.kmRecommendationPosition || value.recommendation_position;
    if (position != null && position !== '' && Number.isFinite(Number(position))) {
      parameters.recommendation_position = Number(position);
    }
    return parameters;
  }

  function trackProduct(name, value) {
    const parameters = product(value);
    if (parameters) track(name, parameters);
  }

  function recommendationAdded(result, variantId, value) {
    if (Array.isArray(result?.items) && result.items.some(item => String(item.variant_id || item.id) === String(variantId)
      && Number(item.quantity) > 0)) trackProduct('add_to_cart_recommendation', value);
  }

  function line(element) {
    const row = element?.closest('[data-km-cart-line]');
    if (!row || row.hasAttribute('data-km-gift-line-item')) return null;
    const parameters = product(row);
    if (!parameters) return null;
    let properties = {};
    try { properties = JSON.parse(row.dataset.kmCartProperties || '{}'); } catch (error) {}
    return {...parameters, key: row.dataset.key, variantId: row.dataset.kmCartVariantId,
      properties, sellingPlan: row.dataset.kmCartSellingPlanId || '',
      quantity: Number(row.querySelector('.js-qty__num')?.dataset.defaultValue)};
  }

  function propertyIdentity(properties) {
    const values = Array.isArray(properties) ? Object.fromEntries(properties) : properties || {};
    return JSON.stringify(Object.keys(values).sort().map(key => [key, values[key]]));
  }

  function quantityResult(before, cart, requestedQuantity, action) {
    if (!before || !Array.isArray(cart?.items)) return;
    const exact = cart.items.find(item => item.key === before.key);
    const candidates = cart.items.filter(item => String(item.variant_id || item.id) === before.variantId
      && propertyIdentity(item.properties) === propertyIdentity(before.properties)
      && String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || '') === before.sellingPlan);
    const current = exact || (candidates.length === 1 ? candidates[0] : null);
    const quantity = Number(requestedQuantity);
    if (!Number.isFinite(before.quantity) || quantity === before.quantity) return;
    if (quantity === 0 ? exact || candidates.length : !current || Number(current.quantity) !== quantity) return;
    const parameters = {product_handle: before.product_handle, product_title: before.product_title};
    if (action !== 'remove') parameters.change_type = quantity > before.quantity ? 'increase' : 'decrease';
    track(action === 'remove' ? 'delete_cart_item' : 'cart_product_change', parameters);
  }

  const active = scope => scope && (scope.id !== 'CartDrawer' || scope.classList.contains('drawer--is-open'));
  const covered = () => Boolean(document.querySelector('[data-km-pair-dialog]:not([hidden]), [data-km-gift-dialog]:not([hidden])'));
  function visible(node) {
    if (!node?.isConnected || document.visibilityState === 'hidden'
      || node.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    return node.checkVisibility ? node.checkVisibility({checkOpacity: true, checkVisibilityCSS: true})
      : Boolean(node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
  }

  function expose(card, entry) {
    const parameters = product(card);
    const position = card.matches('[data-km-cart-pair-product]') ? 'pair with' : 'ymal';
    const key = parameters && JSON.stringify([card.closest(scopes)?.id, position, parameters.product_handle,
      parameters.recommendation_batch_id || '']);
    const qualified = Boolean(entry?.isIntersecting && entry.intersectionRatio >= .5
      && active(card.closest(scopes)) && visible(card) && !covered() && parameters);
    const existingTimer = exposureTimers.get(card);
    if (!qualified) {
      if (existingTimer) { clearTimeout(existingTimer); exposureTimers.delete(card); }
      return;
    }
    if (exposed.has(key) || existingTimer) return;
    const timer = setTimeout(() => {
      exposureTimers.delete(card);
      const current = intersections.get(card);
      if (!current?.isIntersecting || current.intersectionRatio < .5 || !active(card.closest(scopes))
        || !visible(card) || covered() || !card.isConnected) return;
      const currentParameters = product(card);
      if (currentParameters && !exposed.has(key)
        && track('cart_product_exposure', {...currentParameters, position})) exposed.add(key);
      const recommendationKey = JSON.stringify([card.closest(scopes)?.id, position,
        card.dataset.kmProductId, currentParameters?.recommendation_batch_id || '']);
      if (!recommendationExposed.has(recommendationKey)
        && commerce('recommend_view_item_list', card)) recommendationExposed.add(recommendationKey);
    }, 1000);
    exposureTimers.set(card, timer);
  }

  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => { intersections.set(entry.target, entry); expose(entry.target, entry); });
  }, {threshold: [0, .5]}) : null;

  function sync() {
    frame = null;
    document.querySelectorAll(scopes).forEach(scope => {
      let state = views.get(scope);
      if (!state) { state = {kind: null}; views.set(scope, state); }
      if (!active(scope)) { state.kind = null; return; }
      const nativeCart = scope.querySelector('form')?.kmCartForm;
      if ([...(nativeCart?.cartOperations || [])].some(operation => operation.source === 'refresh')
        || nativeCart?.failedCartOperations?.has('refresh')) return;
      const contents = scope.querySelector('[data-products]');
      const items = contents?.querySelectorAll('[data-km-cart-line]').length || 0;
      const emptyMarkup = contents?.querySelector('.cart__items[data-count="0"]');
      const empty = !items && (emptyMarkup || scope.matches('[data-km-cart-page]'));
      const target = empty ? scope.querySelector('.drawer__cart-empty, [data-km-cart-page-empty]') : contents;
      const kind = empty ? 'view_empty_cart' : items ? 'view_cart' : null;
      if (kind && kind !== state.kind && visible(target) && !covered() && track(kind)) state.kind = kind;
    });
    for (const [card, entry] of intersections) {
      if (!card.isConnected) {
        observer?.unobserve(card);
        intersections.delete(card);
        const timer = exposureTimers.get(card);
        if (timer) clearTimeout(timer);
        exposureTimers.delete(card);
      }
      else expose(card, entry);
    }
    if (observer) document.querySelectorAll(scopes).forEach(scope => scope.querySelectorAll(cards).forEach(card => {
      if (!intersections.has(card)) { intersections.set(card, null); observer.observe(card); }
    }));
  }

  function schedule() {
    if (frame === null) frame = requestAnimationFrame(sync);
  }

  // Capture before the drawer's close handler or coupon handler updates its DOM.
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const scope = target?.closest(scopes);
    if (!active(scope)) return;
    if (target.closest('#CartDrawer .drawer__close-button.js-drawer-close')) track('click_close_cart');
    else if (target.closest('[data-km-cart-continue-shopping]')) track('click_continue_shopping');
    else if (target.closest('[data-km-discount-apply]:not(:disabled)')) {
      if (scope.querySelector('[data-km-discount-input]')?.value.trim()) track('click_apply_cart_coupon');
    } else if (target.closest('.cart__image a[href], a.cart__item-name[href]')) {
      const row = target.closest('[data-km-cart-line]');
      if (row) commerce('click_cart_product', row, {variant_id: row.dataset.kmCartVariantId || ''}, event);
    } else if (target.closest('[data-km-cart-pair-product] [data-km-pair-open], [data-km-recommendation-product] a[href]')) {
      const card = target.closest(cards);
      commerce('recommend_select_item', card, {}, event);
      if (card?.matches('[data-km-recommendation-product]')) trackProduct('click_cart_recommendation_product', card);
    } else if (target.closest('[data-km-selected-product-link], [data-km-selected-product-title]')) {
      const summary = target.closest('[data-km-selected-product]');
      if (summary) commerce('click_cart_product', summary, {variant_id: summary.dataset.kmCartVariantId || ''}, event);
    }
  }, true);

  // 等业务处理器执行后再决定是否等待上报：PC 商品选择等已取消跳转的行为保持原样。
  document.addEventListener('click', event => {
    const navigation = navigations.get(event);
    if (!navigation || event.defaultPrevented) return;
    event.preventDefault();
    navigation.promise.then(() => window.location.assign(navigation.link.href));
  });

  function init() {
    const mutation = new MutationObserver(records => {
      if (records.some(record => record.target instanceof Element && (record.target.closest(scopes)
        || record.target.matches('[data-km-pair-dialog], [data-km-gift-dialog]')
        || [...record.addedNodes].some(node => node instanceof Element && (node.matches(scopes) || node.querySelector(scopes)))))) schedule();
    });
    // Observe only relevant attribute names; coalesce DOM updates into one frame.
    mutation.observe(document.body, {childList: true, subtree: true, attributes: true,
      attributeFilter: ['class', 'hidden', 'aria-hidden', 'inert']});
    schedule();
  }

  window.KMCartAnalytics = {track, commerce, product, trackProduct, line, quantityResult, recommendationAdded};
  ['cart:rendered', 'drawerOpen.CartDrawer', 'shopify:section:load', 'shopify:section:unload', 'visibilitychange']
    .forEach(name => document.addEventListener(name, schedule));
  window.addEventListener('pageshow', event => {
    if (event.persisted) document.querySelectorAll(scopes).forEach(scope => views.delete(scope));
    schedule();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
})();
